import { AddItemsProduct } from '@teko/cart/v2';
import { Button, Card, Container, Row, styled } from '@teko/ds-desktop';
import { BuildPcSummary, CustomLink } from '@teko/ecommerce';
import { logger } from '@teko/log';
import { trackCustomEvent } from '@teko/tracking';
import { isEmptyObject } from '@teko/utils';
import { useBuildPCV2, useCartV2 } from 'contexts';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { BuildPCModalMobile } from './BuildPCModal';
import { BuildPcMobileItem } from './BuildPcMobileItem';
import BuildPcMobileTabs from './BuildPcMobileTabs';

export const BuildPCPicker = () => {
  const router = useRouter();
  const {
    buildPCData,
    buildPCDetailProducts,
    changeActiveConfig,
    increaseHardwareQuantity,
    decreaseHardwareQuantity,
    setHardwareQuantity,
    estimatedAmount,
    removeHardware,
    updateCurrentConfig,
  } = useBuildPCV2();
  const { buildPcMapping } = useTenantConfig();
  const { onAddMultipleItems } = useCartV2();
  const [selectedCategory, setSelectedCategory] = useState('');

  const buildPcMappingKeys = useMemo(() => {
    return !!buildPcMapping && !isEmptyObject(buildPcMapping)
      ? Object.keys(buildPcMapping)
      : [];
  }, [buildPcMapping]);

  const onAddConfigurationToCart = async (): Promise<void> => {
    try {
      const configKeys = Object.keys(buildPCData.currentConfig);
      const products: AddItemsProduct[] = [];
      for (let i = 0; i < configKeys.length; i += 1) {
        const key = configKeys[i];
        const productData = buildPCDetailProducts.products.find((product) => {
          return product.sku === buildPCData.currentConfig[key]?.sku;
        });
        const quantity = buildPCData.currentConfig[key]?.quantity;

        if (!!productData && !isEmptyObject(productData) && !!quantity)
          products.push({
            sku: productData.sku,
            quantity,
            promotionId: !!productData.selectPromotionId
              ? productData.selectPromotionId
              : undefined,
          });
      }
      await onAddMultipleItems(products);
    } catch (error) {
      logger.log(error);
    }
  };

  if (!buildPcMapping || isEmptyObject(buildPcMapping)) {
    return null;
  }

  return (
    <Container>
      <div style={{ padding: '0 1rem' }}>
        <Row marginTop="s16" justify="space-between">
          <BuildPcMobileTabs
            lsActiveTab={buildPCData.activeIndex}
            onClickTab={(index) => {
              changeActiveConfig(index);
              trackCustomEvent('changeBuildPCConfig', {
                action: 'click',
                category: 'buildpc',
                value: index + 1,
              });
            }}
            onResetConfig={() => updateCurrentConfig({})}
          />
        </Row>
        <Row marginTop="s16">
          {buildPcMappingKeys.map((buildPcMappingKey, index) => {
            const selectedHardware =
              buildPCData.currentConfig[buildPcMappingKey];
            const productData = buildPCDetailProducts.products.find(
              (product) => {
                return product.sku === selectedHardware?.sku;
              },
            );

            return (
              <StyledBuildPcItem
                key={`${index}-${buildPcMappingKey}`}
                title={buildPcMapping[buildPcMappingKey].name}
                icon={buildPcMapping[buildPcMappingKey].icon}
                onIncrease={() => {
                  increaseHardwareQuantity(buildPcMappingKey);
                }}
                onDecrease={() => {
                  // remove hardware from config when quantity < 1
                  selectedHardware?.quantity === 1
                    ? removeHardware(buildPcMappingKey)
                    : decreaseHardwareQuantity(buildPcMappingKey);
                }}
                setQuantity={(value) => {
                  setHardwareQuantity(buildPcMappingKey, value);
                }}
                product={
                  !!productData && !isEmptyObject(productData)
                    ? productData
                    : undefined
                }
                actionText={productData ? t('Sửa') : t('Chọn')}
                onClick={() => setSelectedCategory(buildPcMappingKey)}
                quantity={selectedHardware?.quantity}
                showDeleteButton
                onDelete={() => removeHardware(buildPcMappingKey)}
              />
            );
          })}
          {!!selectedCategory && (
            <BuildPCModalMobile
              visible={!!selectedCategory}
              collectionSlug={buildPcMapping[selectedCategory]?.slug || ''}
              categoryName={selectedCategory}
              onClose={() => setSelectedCategory('')}
            />
          )}
        </Row>
        <Row marginTop="s8">
          <StyledBuildPcSummary>
            <BuildPcSummary
              total={estimatedAmount}
              selectedCount={Object.keys(buildPCData.currentConfig).length}
              onAddToCart={onAddConfigurationToCart}
              onBuy={(): void => {
                onAddConfigurationToCart().then(() => router.push('/cart'));
              }}
            />
            <Card marginTop="s16" padding="s16">
              <Button
                variant="primary"
                block
                onClick={(): void => window.print()}
                marginBottom="s16"
              >
                {t('Tải file PDF cấu hình')}
              </Button>
              <CustomLink
                targetType="_blank"
                targetPath="https://www.messenger.com/t/phongvuvietnam"
              >
                <Button variant="primary" block>
                  {t('Nhận tư vấn từ chuyên gia')}
                </Button>
              </CustomLink>
            </Card>
          </StyledBuildPcSummary>
        </Row>
      </div>
    </Container>
  );
};

const StyledBuildPcItem = styled(BuildPcMobileItem)`
  margin-bottom: 1rem;
  width: 100%;
`;
const StyledBuildPcSummary = styled.div`
  margin-bottom: 1rem;
  width: 100%;
`;
